import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChallengeData } from '@fitzaa/shared';
import { ChallengeProgressData } from '../types/challenge';
import { useNotification } from '../hooks/useNotification';
import { NotificationContainer } from '../components/NotificationContainer';
import { ChallengeProgressView } from '../components/ChallengeProgressView';
import { LeaderboardEntry } from '@fitzaa/shared';
import { ChallengeLeaderboard } from '../components/ChallengeLeaderboard';
import { getChallengeProgressWeb, updateChallengeProgressWeb, getChallengeLeaderboardWeb, dropChallengeWeb } from '../services/WebCloudFunctionsService';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Header from '../components/Header';
import { ProgressUpdateAnimation } from '../components/ProgressUpdateAnimation';
import { FitzaaLoader } from '../components/FitzaaLoader';
import { ChallengeWall } from '../components/ChallengeWall';
import { FamilyMemberManagement } from '../components/FamilyMemberManagement';

export const ChallengeProgressScreen: React.FC = () => {
    const { challengeId } = useParams<{ challengeId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { theme } = useTheme();
    const { notifications, showSuccess, showError, removeNotification } = useNotification();

    const [challenge, setChallenge] = useState<ChallengeData | null>(null);
    const [progressData, setProgressData] = useState<ChallengeProgressData | null>(null);
    const [showLeaderboard, setShowLeaderboard] = useState(false);
    const [leaderboardEntries, setLeaderboardEntries] = useState<LeaderboardEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);
    const [showProgressAnimation, setShowProgressAnimation] = useState(false);
    const [lastUpdatedValue, setLastUpdatedValue] = useState(0);
    const [activeTab, setActiveTab] = useState('progress');
    // Detect mobile screen size – must be declared before early returns
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        // Check if we're on the photos route and set active tab accordingly
        const isPhotosRoute = window.location.pathname.includes('/photos');
        if (isPhotosRoute) {
            setActiveTab('photos');
        }
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // Load challenge and progress data from real API
        const loadChallengeData = async () => {
            if (!challengeId) return;

            try {
                setIsLoading(true);
                const result = await getChallengeProgressWeb(challengeId);
                const data = result as any;

                if (data.success && data.challenge && data.progressData) {
                    setChallenge(data.challenge);
                    setProgressData(data.progressData);

                    // Debug: Log the loaded progress data
                    console.log('🔍 Web loaded progress data:', {
                        challengeId: data.challenge.id,
                        dailyCompletionsKeys: data.progressData.dailyCompletions ? Object.keys(data.progressData.dailyCompletions) : 'null',
                        dailyCompletions: data.progressData.dailyCompletions,
                        currentValue: data.progressData.currentValue,
                        pointsEarned: data.progressData.pointsEarned
                    });
                } else {
                    console.error('Invalid response format:', data);
                }
            } catch (error) {
                console.error('Failed to load challenge data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadChallengeData();
    }, [challengeId]);

    const handleUpdateProgress = useCallback(async (value: number, date?: string) => {
        if (!challenge) return;

        try {
            const targetDate = date || new Date().toISOString().split('T')[0];

            const result = await updateChallengeProgressWeb(challenge.id, value, targetDate);

            // Update local progress data immediately for better UX
            setProgressData(prev => {
                if (!prev) return prev;

                const isToday = targetDate === new Date().toISOString().split('T')[0];

                // Calculate points based on actual challenge duration (matching Firebase function logic)
                const startDate = new Date(challenge.startDate);
                const endDate = new Date(challenge.endDate);
                const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                const pointsPerDay = Math.round(challenge.points / totalDays);
                const pointsEarned = value >= challenge.targetValue ? pointsPerDay : 0;

                const updatedData = {
                    ...prev,
                    // Only update currentValue and progress if updating today
                    ...(isToday && {
                        currentValue: value,
                        progress: challenge?.targetValue > 0 ? Math.min(value / challenge.targetValue, 1) : 0,
                        lastUpdated: new Date().toISOString(),
                    }),
                    dailyCompletions: {
                        ...prev.dailyCompletions,
                        [targetDate]: {
                            date: targetDate,
                            isCompleted: value >= challenge.targetValue,
                            points: pointsEarned,
                            targetValue: challenge.targetValue,
                            value,
                            completedAt: value >= challenge.targetValue ? new Date().toISOString() : null
                        }
                    }
                };

                // OPTIMIZED: Calculate updated total points earned efficiently
                // Instead of looping through all dailyCompletions, calculate the delta
                const existingPoints = prev?.pointsEarned || 0;
                const newPoints = value >= challenge.targetValue ? pointsPerDay : 0;

                // Get previous points for this date if it exists
                const previousCompletion = prev?.dailyCompletions?.[targetDate];
                const previousPoints = previousCompletion?.points || 0;

                // Calculate new total: existing - previous + new
                const totalPointsEarned = existingPoints - previousPoints + newPoints;
                updatedData.pointsEarned = totalPointsEarned;

                // Debug: Log the optimistic update details
                console.log('🔍 Web optimistic update details:', {
                    targetDate,
                    value,
                    targetValue: challenge.targetValue,
                    isCompleted: value >= challenge.targetValue,
                    dailyCompletionsKeys: Object.keys(updatedData.dailyCompletions),
                    completedDays: Object.values(updatedData.dailyCompletions).filter((day: any) => day.isCompleted).length,
                    totalPointsEarned
                });

                return updatedData;
            });

            // Update local state with server response data to ensure consistency
            if ((result as any).success && (result as any).participation) {
                setProgressData((result as any).participation);
            } else {
                // If server response doesn't have participation data, refresh from server
                try {
                    const refreshResult = await getChallengeProgressWeb(challenge.id);
                    if ((refreshResult as any).success && (refreshResult as any).progressData) {
                        setProgressData((refreshResult as any).progressData);
                    }
                } catch (refreshError) {
                    console.error('Failed to refresh progress data:', refreshError);
                }
            }

            // Show the progress animation
            setLastUpdatedValue(value);
            setShowProgressAnimation(true);
        } catch (error) {
            console.error('Failed to update progress:', error);
            showError('Update Failed', 'Failed to update progress. Please try again.');
        }
    }, [challenge, showError]);

    const handleDropChallenge = async () => {
        if (!challenge) return;

        if (window.confirm('Are you sure you want to drop this challenge? This action cannot be undone.')) {
            try {
                await dropChallengeWeb(challenge.id);
                showSuccess('Challenge Dropped', 'The challenge has been dropped successfully.');
                navigate('/challenges');
            } catch (error) {
                console.error('Failed to drop challenge:', error);
                showError('Drop Failed', 'Failed to drop challenge. Please try again.');
            }
        }
    };

    const handleViewLeaderboard = async () => {
        if (!challenge) return;

        setIsLoadingLeaderboard(true);
        try {
            const result = await getChallengeLeaderboardWeb(challenge.id);
            const data = result as any;
            setLeaderboardEntries(data.entries || []);
            setShowLeaderboard(true);
        } catch (error) {
            console.error('Failed to load leaderboard:', error);
            showError('Leaderboard Error', 'Failed to load leaderboard. Please try again.');
        } finally {
            setIsLoadingLeaderboard(false);
        }
    };

    const handleCloseLeaderboard = () => {
        setShowLeaderboard(false);
    };



    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.colors.background }}>
                <FitzaaLoader size="large" message="Loading challenge..." theme={theme} />
            </div>
        );
    }

    if (!challenge || !progressData) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.colors.background }}>
                <div className="text-center">
                    <h1 className="text-2xl font-bold mb-4" style={{ color: theme.colors.text }}>Challenge Not Found</h1>
                    <p className="mb-6" style={{ color: theme.colors.textSecondary }}>The challenge you're looking for doesn't exist or you don't have access to it.</p>
                    <button
                        onClick={() => navigate('/challenges')}
                        className="px-6 py-2 rounded-lg font-semibold transition-colors duration-200"
                        style={{ backgroundColor: theme.colors.primary, color: theme.colors.primaryText }}
                    >
                        Back to Challenges
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: theme.colors.background }}>
            <Header
                title="Challenge Progress"
                showBack={true}
                showMenu={false}
                showProfile={true}
            />

            <div className="container mx-auto" style={{
                padding: isMobile ? '12px' : '16px 24px',
                paddingTop: isMobile ? '12px' : '24px',
                paddingBottom: isMobile ? '12px' : '24px'
            }}>
                {/* Tab Navigation */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
                    <button
                        onClick={() => setActiveTab('progress')}
                        className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${activeTab === 'progress'
                            ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                    >
                        Progress
                    </button>
                    <button
                        onClick={() => setActiveTab('photos')}
                        className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${activeTab === 'photos'
                            ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                    >
                        Photos
                    </button>
                    {user?.authProvider !== 'google' && (
                        <button
                            onClick={() => setActiveTab('family')}
                            className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${activeTab === 'family'
                                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                        >
                            Family
                        </button>
                    )}
                </div>

                {/* Tab Content */}
                {activeTab === 'progress' && (
                    <ChallengeProgressView
                        challenge={challenge}
                        progressData={progressData}
                        onUpdateProgress={handleUpdateProgress}
                        onDropChallenge={handleDropChallenge}
                        onViewLeaderboard={handleViewLeaderboard}
                    />
                )}

                {activeTab === 'photos' && challenge && (() => {
                    // Debug: Log challenge ID being passed
                    console.log('🌐 [ChallengeProgressScreen] Passing challengeId to ChallengeWall:', {
                        urlChallengeId: challengeId,
                        challengeId: challenge.id,
                        challengeTitle: challenge.title
                    });

                    return (
                        <div style={{
                            backgroundColor: theme.colors.surface,
                            borderRadius: isMobile ? '12px' : '16px',
                            padding: isMobile ? '8px' : '24px',
                            border: `1px solid ${theme.colors.border}`,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                        }}>
                            {/* Teams Photo Wall */}
                            <ChallengeWall
                                challengeId={challenge.id}
                                challengeTitle={`${challenge.title} Photos`}
                            />
                        </div>
                    );
                })()}

                {activeTab === 'family' && user && user.authProvider !== 'google' && (
                    <div style={{
                        backgroundColor: theme.colors.surface,
                        borderRadius: isMobile ? '12px' : '16px',
                        padding: isMobile ? '8px' : '24px',
                        border: `1px solid ${theme.colors.border}`,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                    }}>
                        <div style={{ marginBottom: isMobile ? '12px' : '16px' }}>
                            <h3 style={{
                                fontSize: isMobile ? '14px' : '18px',
                                fontWeight: '600',
                                color: theme.colors.text,
                                marginBottom: isMobile ? '6px' : '8px'
                            }}>
                                Family Members
                            </h3>
                            <p style={{
                                fontSize: isMobile ? '11px' : '14px',
                                color: theme.colors.textSecondary
                            }}>
                                Invite family members to join this challenge and compete together!
                            </p>
                        </div>
                        <FamilyMemberManagement
                            orgUserId={user.id}
                            organizationName={user.organizationName || 'Your Organization'}
                        />
                    </div>
                )}

                {showLeaderboard && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
                            <ChallengeLeaderboard
                                challengeId={challenge.id}
                                challengeTitle={challenge.title}
                                entries={leaderboardEntries}
                                currentUserId={user?.id}
                                onClose={handleCloseLeaderboard}
                            />
                        </div>
                    </div>
                )}

                <NotificationContainer
                    notifications={notifications}
                    theme={theme}
                    onRemove={removeNotification}
                />

                <ProgressUpdateAnimation
                    isVisible={showProgressAnimation}
                    onComplete={() => setShowProgressAnimation(false)}
                    theme={theme}
                    value={lastUpdatedValue}
                    totalPoints={challenge.points}
                    // NEW: Pass measurement values for better display
                    measurementValue={lastUpdatedValue}
                    measurementTarget={challenge.targetValue}
                    measurementUnit={challenge.targetUnit}
                    isMeasurementMode={true}
                />
            </div>
        </div>
    );
}; 